/**
 * @file assets/dynamic-content.js
 * @description Cart, info and product panels of the index shell: History
 * API routing (the panel shows the real URL, /cart, /pages/about or
 * /products/<handle>; one pushed entry per open, swapped in place on a switch,
 * popped again on close, none for the page the entry already shows; a legacy
 * #cart hash is migrated in place; a same-page #fragment link moves focus in
 * place and never closes the panel), the 820px column/overlay switch with
 * focus trapping, and the info/product loaders (two DynamicPanelLoader
 * instances that differ only in selectors, extraction and re-init hooks).
 * On a product, page, policy or cart route the layout
 * serves the page open inside its panel (body[data-index-shell-panel]): the
 * loader adopts it, the entry the page loaded with becomes that panel's
 * record, closing it shows the index at the root URL in place, and below
 * 820px the served page, while the entry shows it, is the page itself (page
 * mode: no overlay, no close); what is opened over it behaves as on the index.
 * The one owner of the panels' history entries, focus and document.title: a
 * close returns focus to its trigger, else to <main>, never to <body>.
 * Reads window.ThemeDom and window.DynamicPanelLoader without a fallback.
 */

(function () {
  'use strict';

  const {
    isDesktopViewport,
    onViewportEdgeChange,
    onReady,
    cycleFocusWithin,
    isFocusableElement,
    isPlainPrimaryClick,
    safelyFocus,
    setElementHidden,
    normalizeWhitespace,
    storeUrl
  } = window.ThemeDom;
  const { findContentElement, normalizePathname } = window.DynamicPanelLoader;

  /** Panel names; a legacy `#<name>` hash on load still names one. */
  const PANEL_NAMES = ['cart', 'info', 'product'];
  /** Key of the panel record this script stores in `history.state`. */
  const HISTORY_STATE_KEY = 'phIndexPanel';
  /** Body attributes of the index shell (layout/theme.liquid, index-layout.js). */
  const SHELL_PANEL_ATTRIBUTE = 'data-index-shell-panel';
  const SHELL_ROOT_ATTRIBUTE = 'data-index-shell-root';
  const SHELL_TITLE_ATTRIBUTE = 'data-index-shell-title';
  const SHELL_SECTIONS_ATTRIBUTE = 'data-index-shell-sections';
  /**
   * Marks an element given a temporary tabindex to take focus (a fragment
   * target, or main after a close with no trigger); site-layout.css drops its
   * focus ring.
   */
  const PROGRAMMATIC_FOCUS_ATTRIBUTE = 'data-programmatic-focus';
  const CONTENT_LABELS = {
    cart: 'Cart',
    info: 'About',
    product: 'Product'
  };
  // Paths the info panel opens in place. The four policies are linked as the
  // canonical /policies/<handle> routes set under Settings → Policies; the
  // /pages/<handle> duplicates stay listed with the same labels because they
  // remain the fallback in the footer and the cart while those pages exist.
  // /pages/info is the About page's pre-2026-09-06 path, kept so a link written
  // before the rename still opens in the panel instead of leaving the index.
  const INFO_PAGE_PATH_LABELS = {
    '/pages/about': 'About',
    '/pages/info': 'About',
    '/policies/terms-of-service': 'Terms of service',
    '/policies/privacy-policy': 'Privacy policy',
    '/policies/shipping-policy': 'Shipping policy',
    '/policies/refund-policy': 'Refund policy',
    '/policies/contact-information': 'Contact information',
    '/policies/legal-notice': 'Legal notice',
    '/pages/terms-of-service': 'Terms of service',
    '/pages/privacy-policy': 'Privacy policy',
    '/pages/shipping-policy': 'Shipping policy',
    '/pages/return-policy': 'Refund policy'
  };
  const INFO_PAGE_PATH_SET = new Set(Object.keys(INFO_PAGE_PATH_LABELS));
  // Every page and policy route is served inside the info panel (D-039), so
  // the fetched page's own panel content (the info section with its h1, or
  // the policy container with its h1) comes first; the rest are fallbacks.
  const INFO_CONTENT_SELECTORS = ['[data-dynamic-info-page-content]', '.info-content', '.info-content-wrapper', '[role="main"]'];
  const PRODUCT_CONTENT_SELECTORS = ['.product-container'];
  const PRODUCT_FALLBACK_SELECTORS = ['[role="main"]'];
  const PRODUCT_TITLE_SELECTORS = ['.product-info__title', 'h1'];
  const PRODUCT_CACHE_LIMIT = 20;

  /**
   * Everything that differs between the info and product loaders. `panel` and
   * `isActive` are supplied by initDynamicContent once the DOM is known.
   */
  const PANEL_LOADER_CONFIGS = [
    {
      name: 'info',
      attrPrefix: 'data-dynamic-info',
      defaultLabel: 'About',
      loaderClass: 'index-dynamic-info__loading',
      errorClass: 'index-dynamic-info__error',
      contentClass: 'index-dynamic-info__page-content info-content type-base',
      errorText: 'Could not load content. Redirecting…',
      cacheLimit: 0,
      linkSelector: 'a[href]',
      resolveLink: (link, path) => (INFO_PAGE_PATH_SET.has(path) ? { path, label: INFO_PAGE_PATH_LABELS[path] } : null),
      extract: extractInfoContent
    },
    {
      name: 'product',
      attrPrefix: 'data-dynamic-product',
      defaultLabel: 'Product',
      loaderClass: 'index-dynamic-product__loader',
      errorClass: 'index-dynamic-product__error',
      contentClass: 'index-dynamic-product__page-content',
      errorText: 'Could not load product. Redirecting…',
      cacheLimit: PRODUCT_CACHE_LIMIT,
      linkSelector: 'a[href][data-dynamic-product-link]',
      resolveLink: resolveProductLink,
      extract: extractProductContent,
      onInject: initializeInjectedProductContent
    }
  ];

  onReady(initDynamicContent);

  function initDynamicContent() {
    if (!document.body.classList.contains('index-shell')) {
      return;
    }

    const defaultColumn = document.querySelector('[data-index-column-content="default"]');
    const dynamicColumn = document.querySelector('[data-index-column-content="dynamic"]');
    const mobileOverlay = document.querySelector('[data-dynamic-content-overlay]');
    const accessibilityAnnouncer = document.querySelector('[data-dynamic-content-announcer]');
    const mainContent = document.querySelector('.site-content.main-content');
    const backgroundRegions = [
      document.querySelector('.page-header'),
      mainContent,
      document.querySelector('.page-footer')
    ].filter(Boolean);
    const dynamicPanels = dynamicColumn ? Array.from(dynamicColumn.querySelectorAll('[data-dynamic-content]')) : [];
    const panelByName = new Map(
      dynamicPanels
        .map((panel) => [panel.getAttribute('data-dynamic-content'), panel])
        .filter(([name]) => Boolean(name))
    );
    const cartPanel = panelByName.get('cart') || null;
    const closeButtons = dynamicColumn ? Array.from(dynamicColumn.querySelectorAll('[data-dynamic-content-close]')) : [];
    const cartTriggers = Array.from(document.querySelectorAll('[data-dynamic-content-trigger="cart"]'));

    if (!defaultColumn || !dynamicColumn || !mobileOverlay || !cartPanel) {
      console.warn('Dynamic content containers not found for index template');
      return;
    }

    let activeContent = null;
    let lastTrigger = null;
    let announcementTimer = null;
    // The panel the layout served this page in ('' on the index itself).
    const servedPanelName = document.body.getAttribute(SHELL_PANEL_ATTRIBUTE) || '';
    const isServedPanelRoute = Boolean(servedPanelName);
    // The served page as a panel record ({ name, path }; the cart has no path),
    // compared with what the panel shows to decide page mode, or null.
    const servedRecord = isServedPanelRoute
      ? { name: servedPanelName, path: servedPanelName === 'cart' ? '' : normalizePathname(window.location.pathname) }
      : null;
    // The index entry beneath any panel entry: its URL, history state and
    // title, restored when a panel entry is cleared in place or left. On a
    // served-panel route that is the root URL with no state; its title comes
    // from the body attribute index-layout.js fills from the fetched index.
    const baseUrl = isServedPanelRoute
      ? document.body.getAttribute(SHELL_ROOT_ATTRIBUTE) || storeUrl('')
      : `${window.location.pathname}${window.location.search}`;
    const baseState = isServedPanelRoute ? null : window.history.state;
    const baseTitle = document.title;
    // The cart URL the header link carries (routes.cart_url), used as the
    // cart panel's address; the loaders supply their own page paths.
    const cartUrl = resolveCartUrl(cartTriggers);
    // True while the current panel entry was pushed by this script, so closing
    // can pop it with history.back() instead of leaving it behind. False for an
    // entry the page loaded with (a legacy #cart hash migrated in place), which
    // closing clears in place instead. Mirrored in the entry's state so Back
    // and Forward restore it.
    let entryPushed = false;
    let pendingPopOptions = null;
    // Name of the panel whose next injected page should be announced: set when
    // a link is selected while that panel is already open, because openContent
    // only announces a panel that was closed.
    let pendingLoadAnnouncement = null;

    const loaders = PANEL_LOADER_CONFIGS.map((config) => window.DynamicPanelLoader.create({
      ...config,
      panel: panelByName.get(config.name) || null,
      isActive: () => activeContent === config.name,
      onInject: (container, label) => {
        if (typeof config.onInject === 'function') {
          config.onInject(container);
        }

        syncDocumentTitle();
        announceLoadedContent(config.name, label);
      }
    }));
    const loaderByName = new Map(loaders.map((loader) => [loader.name, loader]));

    loaders.forEach((loader) => {
      if (!loader.panel) {
        console.warn(`Dynamic ${loader.name} panel not found for index template; ${loader.name} links will use default navigation.`);
      }
    });

    cartTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-controls', cartPanel.id);
      trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('click', (event) => {
        // In page mode the served page is the whole page; the cart link navigates.
        if (!isPlainPrimaryClick(event) || isPageMode()) {
          return;
        }

        event.preventDefault();
        requestOpenContent('cart', { trigger });
      });
    });

    // A same-page #fragment link (the skip link, a #section link in a page or
    // product body) would add an entry with no state, whose popstate reads as
    // the index and closes the panel; move to the target in place instead.
    // Registered before the panel links so /pages/about#team on that page
    // scrolls rather than re-selecting the page.
    document.addEventListener('click', (event) => {
      if (!isPlainPrimaryClick(event)) {
        return;
      }

      const fragment = resolveFragmentLink(event);

      if (!fragment) {
        return;
      }

      event.preventDefault();
      moveToFragment(fragment);
    });

    document.addEventListener('click', (event) => {
      if (!isPlainPrimaryClick(event)) {
        return;
      }

      const details = interceptPanelLink(event);

      if (!details) {
        return;
      }

      event.preventDefault();
      pendingLoadAnnouncement = activeContent === details.loader.name ? details.loader.name : null;
      details.loader.select(details);
      requestOpenContent(details.loader.name, { trigger: details.link });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        requestCloseActiveContent();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (!activeContent || isMediaViewerOpen() || isPageMode()) {
        return;
      }

      if (event.key === 'Escape') {
        // Escape inside a field (e.g. the cart note) belongs to the field.
        if (isTextEntryTarget(event.target)) {
          return;
        }

        event.preventDefault();
        requestCloseActiveContent();
        return;
      }

      // The overlay is a dialog: keep Tab inside the open panel.
      if (event.key === 'Tab' && !isDesktopViewport()) {
        event.preventDefault();
        cycleFocusWithin(panelByName.get(activeContent), event.shiftKey);
      }
    });

    // Back and Forward: the entry's state names the panel (and the page it
    // showed); no panel record means the index itself, unless nothing is open
    // (nothing to close, and focus stays where the navigation put it) or the
    // entry is a fragment of the open panel's own page.
    window.addEventListener('popstate', (event) => {
      const syncOptions = pendingPopOptions || {};

      pendingPopOptions = null;

      if (!getPanelState(event.state) && (!activeContent || keepPanelOnFragmentEntry())) {
        return;
      }

      syncFromHistoryState(event.state, syncOptions);
    });

    // A page restored from the back/forward cache keeps this script's state, but
    // the fetched markup may be stale; start the caches fresh.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        loaders.forEach((loader) => loader.clearCache());
      }
    });

    // Only a crossing of the 820px edge changes the presentation; other
    // resizes (a mobile keyboard, a window drag) leave the open panel alone.
    onViewportEdgeChange((isDesktop) => {
      // Info and product are desktop-only panels; leaving the desktop viewport
      // closes them rather than re-hosting them in the overlay (popping the
      // history entry the open pushed, so Back is not left dead). The served
      // page, while it is what the panel shows, becomes the page instead (page
      // mode); a page opened over it is dismissed back onto it.
      if (activeContent && loaderByName.has(activeContent) && !isDesktop && !isPageMode()) {
        dismissPanel(activeContent, { restoreFocus: false });
        return;
      }

      const activePanel = activeContent ? panelByName.get(activeContent) : null;
      const focusedInPanel = isFocusWithin(activePanel) ? document.activeElement : null;

      syncPresentation();

      // Moving the panel between the column and the overlay drops the focus
      // inside it to <body>; put it back. The overlay is a modal dialog, so
      // focus left in the background it now locks moves into it.
      if (focusedInPanel && document.activeElement !== focusedInPanel) {
        safelyFocus(focusedInPanel);
      } else if (!focusedInPanel && document.body.getAttribute('data-dynamic-content-mode') === 'overlay') {
        focusActivePanel();
      }
    });

    // Restore a panel named by the entry the page loaded with (a bfcache
    // return, or a legacy #cart link); on a served-panel route, first take
    // over the page the layout rendered inside its panel.
    migrateLegacyHash();
    adoptServedPanel();
    syncFromHistoryState(window.history.state, { restoreFocus: false, moveFocus: false });

    /**
     * A product, page, policy or cart route served open inside its panel: the
     * loader adopts the served markup as its selection for the page's path,
     * and the entry the page loaded with becomes that panel's record (not
     * pushed by this script, so closing rewrites it to the index in place)
     * unless it already carries one, e.g. a reload of an entry pushed from
     * the index, which keeps `pushed` so closing still pops back to it. A
     * #fragment the page was loaded at stays in the address.
     */
    function adoptServedPanel() {
      if (!isServedPanelRoute || !panelByName.has(servedPanelName)) {
        return;
      }

      const loader = loaderByName.get(servedPanelName) || null;

      if (loader) {
        loader.adopt({
          path: servedRecord.path,
          label: servedPanelName === 'info' ? INFO_PAGE_PATH_LABELS[servedRecord.path] : ''
        });
      }

      // The body was served in the open state; openContent then treats the
      // panel as already open (no announcement, no scroll reset).
      activeContent = servedPanelName;

      if (!getPanelState(window.history.state)) {
        window.history.replaceState(
          buildPanelState(servedPanelName, loader, false),
          document.title,
          `${window.location.pathname}${window.location.search}${window.location.hash}`
        );
      }
    }

    /**
     * Page mode: below 820px, while the open panel shows the page this
     * document was served for, that panel is the page itself. It follows the
     * entry, not the load route: a cart or page opened over the served page
     * (or over the index it closed to) behaves as it would on the index.
     */
    function isPageMode() {
      return Boolean(activeContent) && isPageModeFor(activeContent, getShownPath(activeContent));
    }

    /** Whether panel `name` showing `path` would be in page mode at the current width. */
    function isPageModeFor(name, path) {
      return !isDesktopViewport() && isSameRecord({ name, path }, servedRecord);
    }

    /** The page a panel shows: its loader's selection, or '' for the cart. */
    function getShownPath(contentName) {
      const loader = loaderByName.get(contentName);

      return loader ? loader.getPath() : '';
    }

    /** The index title: the fetched index's on a served-panel route, else the page's own. */
    function getBaseTitle() {
      return isServedPanelRoute ? document.body.getAttribute(SHELL_TITLE_ATTRIBUTE) || baseTitle : baseTitle;
    }

    /** Details from the first usable loader that claims the clicked link. */
    function interceptPanelLink(event) {
      for (const loader of loaders) {
        const details = loader.canUse() ? loader.intercept(event) : null;

        if (details) {
          return details;
        }
      }

      return null;
    }

    /**
     * Open a panel at its own URL. From the index this pushes one entry;
     * while a panel is open (a switch, or a second page in the same panel)
     * the entry is rewritten in place, so Back still leaves the panel state
     * entirely instead of stepping through each page shown. The entry a
     * served-panel route loaded with is the page itself, so the first thing
     * opened over it is pushed too, and Back returns to the served page; the
     * pushed record names that page (`over`, kept through rewrites), so asking
     * for it again goes back to it rather than turning the pushed entry into
     * a copy of it. Asking for the page the entry already records (the header
     * Cart link on /cart, a product's own card on its page) changes no
     * history, so the first Close or Back still does something visible.
     */
    function requestOpenContent(contentName, options = {}) {
      const nextPanel = panelByName.get(contentName);
      const loader = loaderByName.get(contentName);

      if (!nextPanel || (loader && !loader.canUse())) {
        return;
      }

      if (options.trigger && document.contains(options.trigger)) {
        lastTrigger = options.trigger;
      }

      const targetUrl = loader ? loader.getPath() : cartUrl;
      const currentRecord = getPanelState(window.history.state);
      const requestedRecord = { name: contentName, path: loader ? loader.getPath() : '' };
      const onServedEntry = isServedPanelRoute && !entryPushed && currentRecord !== null;

      if (isSameRecord(currentRecord, requestedRecord)) {
        openContent(contentName, options);
        return;
      }

      if (isServedPanelRoute && entryPushed && currentRecord && isSameRecord(currentRecord.over, requestedRecord)) {
        // The served page is the entry beneath this one: step back onto it
        // (its popstate shows it) as Close would.
        entryPushed = false;
        window.history.back();
        return;
      }

      if (activeContent && !onServedEntry) {
        window.history.replaceState(
          buildPanelState(contentName, loader, entryPushed, currentRecord && currentRecord.over),
          document.title,
          targetUrl
        );
      } else {
        entryPushed = true;
        window.history.pushState(
          buildPanelState(contentName, loader, true, onServedEntry ? currentRecord : null),
          document.title,
          targetUrl
        );
      }

      openContent(contentName, options);
    }

    function openContent(contentName, options = {}) {
      const nextPanel = panelByName.get(contentName);

      if (!nextPanel) {
        return;
      }

      const wasAlreadyOpen = activeContent === contentName;

      // A viewer opened from the product panel must not outlive the switch to
      // another panel.
      if (activeContent && !wasAlreadyOpen) {
        closeMediaViewer();
      }

      activeContent = contentName;
      syncPresentation();
      syncDocumentTitle();
      // Every open or new selection starts at the top of the host.
      (isDesktopViewport() ? dynamicColumn : mobileOverlay).scrollTop = 0;

      if (options.moveFocus !== false && (!wasAlreadyOpen || !isFocusWithin(nextPanel))) {
        focusActivePanel();
      }

      if (!wasAlreadyOpen) {
        announceContentState(contentName, 'open');
      }
    }

    /** Close button and Escape: leave the open panel, whatever backs it (never in page mode). */
    function requestCloseActiveContent(options = {}) {
      if (activeContent && !isPageMode()) {
        dismissPanel(activeContent, options);
      }
    }

    /**
     * Pop the entry this script pushed when opening, so Back stays symmetric;
     * the resulting popstate closes the panel with `options`. Returns false
     * when the current entry was not pushed here (the page loaded with it),
     * in which case the caller clears the entry in place.
     */
    function popPushedEntry(options) {
      if (!entryPushed) {
        return false;
      }

      entryPushed = false;
      pendingPopOptions = { restoreFocus: options.restoreFocus, moveFocus: false };
      window.history.back();
      return true;
    }

    /**
     * Leave a panel: drop a loader's in-flight fetch (or, with `reset`, its
     * selection), leave its history entry (popping it when this script pushed
     * it, rewriting it to the index in place when the page loaded with it)
     * and close whatever is open.
     */
    function dismissPanel(contentName, options = {}) {
      const loader = loaderByName.get(contentName);
      const restoreFocus = options.restoreFocus !== false;

      if (loader) {
        if (options.reset) {
          loader.reset();
        } else {
          loader.cancel();
        }
      }

      if (getPanelNameFromState(window.history.state) === contentName) {
        if (popPushedEntry({ restoreFocus })) {
          return;
        }

        // A served-panel route whose catalogue never arrived has no index to
        // show in place; go to the real one.
        if (isServedPanelRoute && document.body.getAttribute(SHELL_SECTIONS_ATTRIBUTE) !== 'loaded') {
          window.location.assign(baseUrl);
          return;
        }

        window.history.replaceState(baseState, getBaseTitle(), baseUrl);
      }

      closeActiveContent({ restoreFocus });
    }

    /**
     * Close whatever is open. With `restoreFocus` (the default) focus returns
     * to the trigger, else to main; without it focus is left where it is,
     * unless it was inside the panel being hidden (a dismissal on resize),
     * which would drop it to <body>.
     */
    function closeActiveContent(options = {}) {
      const restoreFocus = options.restoreFocus !== false;
      const contentToClose = activeContent;
      const focusWasInPanel = isFocusWithin(contentToClose ? panelByName.get(contentToClose) : null);

      // Nothing should land in a panel that is no longer shown, and a viewer
      // opened from the product panel closes with it.
      loaders.forEach((loader) => loader.cancel());
      closeMediaViewer();

      activeContent = null;
      entryPushed = false;
      pendingLoadAnnouncement = null;

      syncPresentation();
      syncDocumentTitle();

      if (contentToClose) {
        announceContentState(contentToClose, 'close');
      }

      if (restoreFocus || focusWasInPanel) {
        focusAfterClose(restoreFocus);
      }
    }

    /**
     * Focus after a panel closes: the control that opened it while it is
     * still on screen, else main, as the skip link does. A close with no
     * trigger (a served page, a Forward to a panel, a trigger the panel's own
     * content replaced or a hidden panel holds) never leaves focus on <body>.
     */
    function focusAfterClose(useTrigger) {
      if (useTrigger && lastTrigger && isFocusableElement(lastTrigger)) {
        safelyFocus(lastTrigger);

        if (document.activeElement === lastTrigger) {
          return;
        }
      }

      if (mainContent) {
        focusInPlace(mainContent);
      }
    }

    /**
     * Show what a history entry's state names: a panel record opens that
     * panel with the page it recorded (a loader whose selection differs, or
     * whose fetch was cancelled on close, loads it again); anything else is
     * the index.
     */
    function syncFromHistoryState(state, options = {}) {
      const restoreFocus = options.restoreFocus !== false;
      const moveFocus = options.moveFocus !== false;
      const panelState = getPanelState(state);
      const contentName = panelState ? panelState.name : null;
      const loader = contentName ? loaderByName.get(contentName) : null;

      if (!contentName || !panelByName.has(contentName)) {
        closeActiveContent({ restoreFocus });
        return;
      }

      entryPushed = Boolean(panelState.pushed);

      if (loader) {
        // Desktop-only panels, except that in page mode the panel is the page.
        if (!loader.canUse() && !isPageModeFor(contentName, panelState.path)) {
          dismissPanel(contentName, { restoreFocus: false });
          return;
        }

        if (!panelState.path) {
          // A panel entry with no page recorded has nothing to show.
          dismissPanel(contentName, { reset: true, restoreFocus });
          return;
        }

        loader.restore({ path: panelState.path, label: panelState.label });
      }

      openContent(contentName, { moveFocus });
    }

    /**
     * A fragment navigation the click handler did not take over (a typed
     * #hash, a link whose target is missing, a script setting location.hash)
     * adds an entry with no state at the open panel's own path. It still
     * shows that page, so the panel stays open and the entry gets a record of
     * its own. The record says `pushed: false`: the browser added the entry,
     * and popping it would land on the entry beneath, which shows the same
     * page, so the first Close would look dead; unpushed, Close rewrites it
     * to the index in place. Returns false when the entry is not such a
     * fragment, leaving it to close the panel as before.
     */
    function keepPanelOnFragmentEntry() {
      const loader = activeContent ? loaderByName.get(activeContent) || null : null;
      const panelPath = loader ? loader.getPath() : cartUrl;

      if (!activeContent || !panelPath || normalizePathname(window.location.pathname) !== normalizePathname(panelPath)) {
        return false;
      }

      entryPushed = false;
      window.history.replaceState(buildPanelState(activeContent, loader, false), document.title);
      return true;
    }

    /**
     * Go to a same-page fragment target as the browser would (scroll it to
     * the top, move focus to it), but record the hash on the current entry
     * with replaceState: no new entry, so the entry keeps its panel record and
     * Back keeps its meaning.
     */
    function moveToFragment({ element, url }) {
      window.history.replaceState(window.history.state, document.title, url);
      element.scrollIntoView({ block: 'start', inline: 'nearest' });
      focusInPlace(element);
    }

    /**
     * Focus an element without scrolling to it. One that cannot take focus
     * (main#MainContent, for the skip link or after a close with no trigger)
     * gets tabindex="-1" until it loses focus again, and no focus ring.
     */
    function focusInPlace(element) {
      safelyFocus(element);

      if (document.activeElement === element || element.hasAttribute('tabindex')) {
        return;
      }

      element.setAttribute('tabindex', '-1');
      element.setAttribute(PROGRAMMATIC_FOCUS_ATTRIBUTE, '');
      safelyFocus(element);

      const clearTemporaryFocus = () => {
        element.removeAttribute('tabindex');
        element.removeAttribute(PROGRAMMATIC_FOCUS_ATTRIBUTE);
      };

      if (document.activeElement === element) {
        element.addEventListener('blur', clearTemporaryFocus, { once: true });
      } else {
        clearTemporaryFocus();
      }
    }

    /**
     * A page loaded at a legacy `#cart` link (the routing before 2026-09-06)
     * opens the cart as an entry this script did not push, at the cart URL;
     * a bare `#info` or `#product` names no page and is cleared in place.
     */
    function migrateLegacyHash() {
      const contentName = getContentFromHash(window.location.hash);

      if (!contentName || getPanelState(window.history.state)) {
        return;
      }

      if (contentName === 'cart') {
        window.history.replaceState(buildPanelState('cart', null, false), document.title, cartUrl);
        return;
      }

      window.history.replaceState(baseState, document.title, baseUrl);
    }

    /**
     * The panel record of a history entry. `over` ({ name, path }) is set
     * only on an entry pushed over a served page's own entry, naming that
     * page, so requestOpenContent can return to it instead of copying it.
     */
    function buildPanelState(contentName, loader, pushed, over) {
      const record = {
        name: contentName,
        path: loader ? loader.getPath() : '',
        label: loader ? loader.getLabel() : '',
        pushed: Boolean(pushed)
      };

      if (over && over.name) {
        record.over = { name: over.name, path: over.path || '' };
      }

      return { [HISTORY_STATE_KEY]: record };
    }

    /**
     * The open panel's page title (once its page has loaded), else the index
     * title; the cart keeps the index title, except that a served cart route
     * keeps its own.
     */
    function syncDocumentTitle() {
      const loader = activeContent ? loaderByName.get(activeContent) : null;
      const pageTitle = loader ? loader.getDocumentTitle() : '';

      if (pageTitle) {
        document.title = pageTitle;
        return;
      }

      document.title = activeContent === 'cart' && servedPanelName === 'cart' ? baseTitle : getBaseTitle();
    }

    /**
     * Three presentations: the desktop column, the mobile overlay (a dialog
     * over the index) and, below 820px while the panel shows the page this
     * document was served for, page mode (isPageMode), where the open panel
     * stays in the column host as the page's content with
     * no overlay, no background lock and no close (index-page.css hides the
     * panel header and the columns in that mode).
     */
    function syncPresentation() {
      const activePanel = activeContent ? panelByName.get(activeContent) : null;
      const isOpen = Boolean(activePanel);
      const isDesktop = isDesktopViewport();
      const pageMode = isOpen && isPageMode();
      const hostInColumn = isDesktop || pageMode;
      const currentHost = hostInColumn ? dynamicColumn : mobileOverlay;

      dynamicPanels.forEach((panel) => {
        if (panel.parentNode !== currentHost) {
          currentHost.appendChild(panel);
        }
      });

      syncCloseButtonState(isDesktop);
      syncPanelSemantics(activePanel, hostInColumn);

      dynamicPanels.forEach((panel) => {
        setHostVisible(panel, panel === activePanel);
      });

      setElementHidden(defaultColumn, isOpen);
      setHostVisible(dynamicColumn, isOpen && hostInColumn);
      setHostVisible(mobileOverlay, isOpen && !hostInColumn);
      syncBackgroundState(isOpen && !hostInColumn);

      document.body.classList.toggle('index-dynamic-content-open', isOpen);

      if (isOpen) {
        document.body.setAttribute('data-active-dynamic-content', activeContent);
        document.body.setAttribute('data-dynamic-content-mode', isDesktop ? 'column' : (pageMode ? 'page' : 'overlay'));
      } else {
        document.body.removeAttribute('data-active-dynamic-content');
        document.body.removeAttribute('data-dynamic-content-mode');
      }

      syncTriggerState(isDesktop);
    }

    /** The cart link opens a dialog only in the mobile overlay presentation; in page mode it navigates. */
    function syncTriggerState(isDesktop) {
      const isCartOpen = activeContent === 'cart';
      const opensDialog = !isDesktop && !isPageMode();

      cartTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', isCartOpen ? 'true' : 'false');

        if (opensDialog) {
          trigger.setAttribute('aria-haspopup', 'dialog');
        } else {
          trigger.removeAttribute('aria-haspopup');
        }
      });
    }

    /** The close buttons carry both presentations' text and label as data attributes. */
    function syncCloseButtonState(isDesktop) {
      const mode = isDesktop ? 'desktop' : 'mobile';

      closeButtons.forEach((button) => {
        button.textContent = button.getAttribute(`data-dynamic-content-close-text-${mode}`);
        button.setAttribute('aria-label', button.getAttribute(`data-dynamic-content-close-label-${mode}`));
      });
    }

    function syncPanelSemantics(activePanel, isDesktop) {
      dynamicPanels.forEach((panel) => {
        if (panel === activePanel) {
          panel.setAttribute('role', isDesktop ? 'region' : 'dialog');
          panel.setAttribute('tabindex', '-1');

          if (isDesktop) {
            panel.removeAttribute('aria-modal');
          } else {
            panel.setAttribute('aria-modal', 'true');
          }

          return;
        }

        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        panel.removeAttribute('tabindex');
      });
    }

    function syncBackgroundState(lockBackground) {
      backgroundRegions.forEach((region) => {
        if (lockBackground) {
          region.setAttribute('inert', '');
          region.setAttribute('aria-hidden', 'true');
        } else {
          region.removeAttribute('inert');
          region.removeAttribute('aria-hidden');
        }
      });
    }

    function focusActivePanel() {
      const activePanel = activeContent ? panelByName.get(activeContent) : null;

      if (!activePanel) {
        return;
      }

      window.requestAnimationFrame(() => {
        const focusTarget = isDesktopViewport() ? activePanel : getPanelCloseButton(activePanel) || activePanel;

        safelyFocus(focusTarget);
      });
    }

    function announceContentState(contentName, state) {
      announce(`${CONTENT_LABELS[contentName]} ${getPresentationLabel()} ${state === 'open' ? 'opened' : 'closed'}`);
    }

    /**
     * After a loader injects a page into a panel that was already open (a
     * second card while the product panel shows one, a policy link while the
     * info panel is up), the title changes silently; say what arrived, e.g.
     * "Kaiser Wilhelmina loaded in product panel".
     */
    function announceLoadedContent(contentName, label) {
      if (pendingLoadAnnouncement !== contentName || activeContent !== contentName) {
        return;
      }

      pendingLoadAnnouncement = null;
      announce(`${label} loaded in ${CONTENT_LABELS[contentName].toLowerCase()} ${getPresentationLabel()}`);
    }

    function getPresentationLabel() {
      return isDesktopViewport() ? 'panel' : 'dialog';
    }

    /** Clear the live region first so a repeated message is still read. */
    function announce(message) {
      if (!accessibilityAnnouncer) {
        return;
      }

      clearTimeout(announcementTimer);
      accessibilityAnnouncer.textContent = '';

      announcementTimer = window.setTimeout(() => {
        accessibilityAnnouncer.textContent = message;
      }, 30);
    }
  }

  /**
   * Product cards: the anchor href is both the page to fetch and the fallback
   * redirect; the card supplies the title so the panel heading needs no fetch.
   */
  function resolveProductLink(link, path) {
    if (path.indexOf('/products/') === -1) {
      return null;
    }

    return {
      path,
      label: link.getAttribute('data-dynamic-product-title')
    };
  }

  /**
   * The info page's content region: the fetched page's own info panel
   * content, i.e. the info section with its h1 or, for a /policies/<handle>
   * route, Shopify's .shopify-policy__container with its h1 (index-page.css
   * styles that root). The page keeps its h1 in the panel (one h1 per view,
   * D-049). No match means null (never the whole page body), so the loader
   * shows its error line and redirects instead.
   */
  function extractInfoContent(doc) {
    const match = findContentElement(doc, INFO_CONTENT_SELECTORS);

    return match ? { html: match.innerHTML } : null;
  }

  /**
   * The product-detail fragment of a product page: the product container
   * first, then the main region only if it holds recognisable product markup.
   */
  function extractProductContent(doc, fallbackTitle) {
    const productElement = findContentElement(doc, PRODUCT_CONTENT_SELECTORS)
      || findContentElement(doc, PRODUCT_FALLBACK_SELECTORS, containsProductDetailMarkup);

    if (!productElement) {
      return null;
    }

    return {
      html: productElement.outerHTML,
      title: extractProductTitle(doc, productElement, fallbackTitle)
    };
  }

  function containsProductDetailMarkup(element) {
    return element.querySelector('.product-info, form[action*="/cart/add"]') !== null;
  }

  function extractProductTitle(doc, productElement, fallbackTitle) {
    for (const root of [productElement, doc]) {
      for (const selector of PRODUCT_TITLE_SELECTORS) {
        const titleElement = root.querySelector(selector);
        const titleText = normalizeWhitespace(titleElement && titleElement.textContent);

        if (titleText) {
          return titleText;
        }
      }
    }

    const documentTitle = String(doc.title || '').split(/[–—|-]/)[0].trim();

    return documentTitle || fallbackTitle || 'Product';
  }

  /**
   * Re-run the behaviours the product template gets on load. Pictures load natively and the gallery is a
   * <media-gallery> element (media-viewer.js), which sets itself up when inserted.
   */
  function initializeInjectedProductContent(container) {
    if (window.CartRefreshOrchestrator && typeof window.CartRefreshOrchestrator.initializeBehaviors === 'function') {
      window.CartRefreshOrchestrator.initializeBehaviors(container);
    }
  }

  /** True while the fullscreen image viewer (a modal <dialog>, media-viewer.js) is open. */
  function isMediaViewerOpen() {
    return Boolean(document.querySelector('dialog.media-viewer[open]'));
  }

  /** Close the fullscreen image viewer at once: the panel it was opened from is going away. */
  function closeMediaViewer() {
    document.dispatchEvent(new CustomEvent('media-viewer:close'));
  }

  /**
   * Hosts and panels must carry an explicit aria-hidden="false" while shown:
   * index-page.css keys the panel enter animations on that value, so this
   * cannot go through ThemeDom.setElementHidden (which removes the attribute).
   */
  function setHostVisible(element, visible) {
    element.hidden = !visible;
    element.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  /** The panel named by a legacy location hash (`#Cart` counts as `#cart`), or null. */
  function getContentFromHash(hashValue) {
    const contentName = String(hashValue || '')
      .trim()
      .replace(/^#+/, '')
      .toLowerCase();

    return PANEL_NAMES.includes(contentName) ? contentName : null;
  }

  /** The panel record this script stored in a history state, or null. */
  function getPanelState(state) {
    const panelState = state && typeof state === 'object' ? state[HISTORY_STATE_KEY] : null;

    return panelState && PANEL_NAMES.includes(panelState.name) ? panelState : null;
  }

  function getPanelNameFromState(state) {
    const panelState = getPanelState(state);

    return panelState ? panelState.name : null;
  }

  /** True when two panel records (or `over` references) name the same panel and page. */
  function isSameRecord(record, other) {
    return Boolean(record && other)
      && record.name === other.name
      && (record.path || '') === (other.path || '');
  }

  /**
   * `{ element, url }` for a click on a same-document fragment link (same
   * path and query as the address, a non-empty #fragment, no new window or
   * download) whose target exists, or null for anything the browser should
   * handle itself, a missing target included.
   */
  function resolveFragmentLink(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;

    if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) {
      return null;
    }

    const targetValue = String(link.getAttribute('target') || '').trim().toLowerCase();

    if (targetValue && targetValue !== '_self') {
      return null;
    }

    let url;

    try {
      url = new URL(link.href);
    } catch (error) {
      return null;
    }

    const { origin, pathname, search } = window.location;

    if (url.origin !== origin || url.pathname !== pathname || url.search !== search || url.hash.length < 2) {
      return null;
    }

    const element = findFragmentTarget(url.hash.slice(1));

    return element ? { element, url: `${url.pathname}${url.search}${url.hash}` } : null;
  }

  /**
   * The element a fragment names, looked up as the browser does: an id, then
   * an `<a name>`, first as written and then percent-decoded.
   */
  function findFragmentTarget(fragment) {
    let decoded = fragment;

    try {
      decoded = decodeURIComponent(fragment);
    } catch (error) {
      // A malformed escape is only looked up as written.
    }

    for (const name of [fragment, decoded]) {
      const element = document.getElementById(name)
        || Array.from(document.getElementsByName(name)).find((candidate) => candidate instanceof HTMLAnchorElement);

      if (element) {
        return element;
      }
    }

    return null;
  }

  /** The cart path from the header trigger's href (routes.cart_url), else the store's /cart. */
  function resolveCartUrl(triggers) {
    const trigger = triggers.find((element) => element.getAttribute('href'));

    if (trigger) {
      try {
        return new URL(trigger.getAttribute('href'), window.location.origin).pathname;
      } catch (error) {
        // Fall through to the default route.
      }
    }

    return storeUrl('cart');
  }

  function getPanelCloseButton(panel) {
    return panel ? panel.querySelector('[data-dynamic-content-close]') : null;
  }

  function isFocusWithin(container) {
    return Boolean(container && container.contains(document.activeElement));
  }

  /** A control that consumes Escape itself: text-like inputs, textarea, select, contenteditable. */
  function isTextEntryTarget(target) {
    if (!(target instanceof Element)) {
      return false;
    }

    if (target instanceof HTMLInputElement) {
      return !/^(button|submit|reset|checkbox|radio|file|image|range|color|hidden)$/.test(target.type);
    }

    return target.matches('textarea, select') || (target instanceof HTMLElement && target.isContentEditable);
  }
})();
